
export interface ROICalculation {
  roi: string;
  roiValue: number;
  confidence: 'high' | 'medium' | 'low';
  factors: {
    locationScore: number;
    developerScore: number;
    priceScore: number;
    timelineScore: number;
  };
  trend: 'up' | 'flat' | 'down';
}

// Location Premiums (approximate historical appreciation + demand)
const LOCATION_SCORES: Record<string, number> = {
  'Palm Jumeirah': 18,
  'Downtown Dubai': 15,
  'Dubai Marina': 14,
  'Business Bay': 12,
  'Jumeirah Beach Residence': 13,
  'JBR': 13,
  'Dubai Hills Estate': 11,
  'Dubai Hills': 11,
  'MBR City': 10,
  'Meydan': 10,
  'Jumeirah Village Circle': 8,
  'JVC': 8,
  'Dubai South': 7,
  'Dubai Creek Harbour': 12,
  'Sobha Hartland': 11,
  'Arjan': 7,
  'Jumeirah Lake Towers': 9,
  'JLT': 9,
};

// Developer Tiers
const DEVELOPER_TIERS: Record<string, number> = {
  // Tier 1 (Premium/Established)
  'Emaar': 3,
  'Sobha': 3,
  'Damac': 2.5,
  'Nakheel': 3,
  'Meraas': 3,
  'Select Group': 2.5,
  'Omniyat': 3,
  'Binghatti': 2,
  'Danube': 2,
  'Azizi': 1.5,
  'Ellington': 2.5,
};

export const calculateROI = (
  location: string,
  developer: string,
  pricePerSqft: number,
  completionDate?: string
): ROICalculation => {
  // 1. Location Factor (40% weight)
  // Normalize location string to match keys
  const normalizedLoc = Object.keys(LOCATION_SCORES).find(k => 
    location.toLowerCase().includes(k.toLowerCase())
  );
  const locationBase = normalizedLoc ? LOCATION_SCORES[normalizedLoc] : 9; // Default 9%
  
  // 2. Developer Factor (20% weight)
  const normalizedDev = Object.keys(DEVELOPER_TIERS).find(k => 
    developer.toLowerCase().includes(k.toLowerCase())
  );
  const developerBonus = normalizedDev ? DEVELOPER_TIERS[normalizedDev] : 0;

  // 3. Timeline Factor (15% weight)
  // Projects closer to completion usually have lower remaining appreciation potential (but safer)
  // Off-plan projects further out have higher potential (but higher risk)
  // We'll reward long-term off-plan slightly more for "Projected ROI"
  let timelineBonus = 0;
  if (completionDate) {
    const today = new Date();
    const completion = new Date(completionDate);
    // If completion is valid date
    if (!isNaN(completion.getTime())) {
      const yearsToCompletion = (completion.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 365);
      if (yearsToCompletion > 3) timelineBonus = 2; // High growth potential
      else if (yearsToCompletion > 1) timelineBonus = 1; // Moderate
      else timelineBonus = 0.5; // Near completion
    }
  }

  // 4. Price Factor (25% weight)
  // This is hard to calc without area avg, but we can assume lower price/sqft has higher growth room
  // This is a rough heuristic
  let priceBonus = 0;
  if (pricePerSqft > 0) {
    if (pricePerSqft < 1500) priceBonus = 2; // Undervalued/Emerging
    else if (pricePerSqft < 2500) priceBonus = 1; // Standard
    else priceBonus = 0.5; // Premium already
  }

  // Calculate Total Projected ROI (over ~5 years implied)
  // We sum the factors. This is an ESTIMATE.
  // Base Market Growth (5%) + Location + Developer + Timeline + Price
  const baseMarketGrowth = 5;
  const totalROI = baseMarketGrowth + locationBase + developerBonus + timelineBonus + priceBonus;
  
  // Cap reasonable limits
  const finalROI = Math.min(Math.max(totalROI, 6), 25); // Between 6% and 25%

  return {
    roi: `+${finalROI.toFixed(1)}%`,
    roiValue: finalROI,
    confidence: normalizedLoc ? 'high' : 'medium',
    factors: {
      locationScore: locationBase,
      developerScore: developerBonus,
      priceScore: priceBonus,
      timelineScore: timelineBonus
    },
    trend: finalROI > 12 ? 'up' : 'flat'
  };
};
